// Story 7.10 (ADO-906), AC1/AC3/AC4 — active filter chips (Figma 608:3184).
// Each active (facet, value) selection renders as an individually removable chip;
// a Clear-all link (Figma 608:3192) restores the unfiltered set (AC4). Renders
// nothing when no filter is active. Shared by the Browse listing + Search hosts.
// Epic 7 demo polish (2026-07-17): the taxonomy drill selection (Department /
// Category / Sub-Category) also renders as a leading chip (prototype
// ActiveFilterChips derives chips from department + categories too).

import {
  activeFilterChips,
  FACET_LABELS,
  facetValueLabel,
  type FacetKey,
  type ProductFilters,
} from '../../api/facets';
import { Icon } from '../../design-system';
import styles from './FilterChips.module.css';

export interface DrillChip {
  /** Display label of the selected taxonomy node (already display-cased). */
  readonly label: string;
  /** Dismiss the drill selection (back to the all-products root). */
  readonly onRemove: () => void;
}

export interface FilterChipsProps {
  readonly filters: ProductFilters;
  readonly onRemove: (key: FacetKey, value: string) => void;
  readonly onClearAll: () => void;
  /** The taxonomy drill selection, rendered as the leading chip (optional). */
  readonly drillChip?: DrillChip | undefined;
  /** Story 8.1 (ADO-2010): the landing-tile scope (a tile's categories, an LSN
   *  category, Catering only), each an individually removable chip after the drill. */
  readonly scopeChips?: readonly DrillChip[] | undefined;
  /** Optional class on the chips row — hosts use it for prototype spacing. */
  readonly className?: string | undefined;
}

export function FilterChips({
  filters,
  onRemove,
  onClearAll,
  drillChip,
  scopeChips = [],
  className,
}: FilterChipsProps) {
  const chips = activeFilterChips(filters);
  if (chips.length === 0 && drillChip === undefined && scopeChips.length === 0) return null;

  return (
    <div
      className={[styles.chips, className].filter(Boolean).join(' ')}
      role="group"
      aria-label="Active filters"
    >
      <ul className={styles.list}>
        {drillChip !== undefined && (
          <li>
            <button
              type="button"
              className={styles.chip}
              onClick={drillChip.onRemove}
              aria-label={`Remove filter: ${drillChip.label}`}
            >
              <span className={styles.chipText}>{drillChip.label}</span>
              <span className={styles.remove} aria-hidden="true">
                ×
              </span>
            </button>
          </li>
        )}
        {scopeChips.map((scopeChip) => (
          <li key={`scope:${scopeChip.label}`}>
            <button
              type="button"
              className={styles.chip}
              onClick={scopeChip.onRemove}
              aria-label={`Remove filter: ${scopeChip.label}`}
            >
              <span className={styles.chipText}>{scopeChip.label}</span>
              <span className={styles.remove} aria-hidden="true">
                ×
              </span>
            </button>
          </li>
        ))}
        {chips.map(({ key, value }) => (
          <li key={`${key}:${value}`}>
            <button
              type="button"
              className={styles.chip}
              onClick={() => onRemove(key, value)}
              aria-label={`Remove filter ${FACET_LABELS[key]}: ${facetValueLabel(key, value)}`}
            >
              <span className={styles.chipText}>{facetValueLabel(key, value)}</span>
              <span className={styles.remove} aria-hidden="true">
                ×
              </span>
            </button>
          </li>
        ))}
      </ul>
      <button type="button" className={styles.clearAll} onClick={onClearAll}>
        <Icon name="clockCounterClockwise" size={16} />
        Clear filters
      </button>
    </div>
  );
}
